import { FileUpload } from "@admin-ui/components/application/file-upload/file-upload-base";
import { Badge } from "@admin-ui/components/primitives/base/badges/badges";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { ArrowLeft, ArrowRight, Plus, Trash01 } from "@untitledui/icons";
import { useState } from "react";
import { StockPhotoSearchModal } from "@/components/admin/StockPhotoSearchModal";
import type { AdminPhoto, AdminPhotoManager } from "@/hooks/admin/use-admin-photo-manager";

interface PhotoSectionProps<P extends AdminPhoto> {
  manager: AdminPhotoManager<P>;
}

export function PhotoSection<P extends AdminPhoto>({ manager }: PhotoSectionProps<P>) {
  const [isStockSearchOpen, setIsStockSearchOpen] = useState(false);
  const {
    photos,
    upload,
    isUploadModalOpen,
    openUploadModal,
    closeUploadModal,
    confirmUpload,
    hasStockPhotoSupport,
    stockSearchPath,
  } = manager;

  const handleFileDrop = (files: FileList) => {
    upload.addFiles(Array.from(files));
  };

  const handleStockPhotoSelect = (urlOriginal: string, _urlPreview: string) => {
    setIsStockSearchOpen(false);
    manager.submitStockPhoto(urlOriginal);
  };

  const swapPhotos = (a: number, b: number) => {
    const ids = photos.map((p) => p.id);
    const temp = ids[a];
    ids[a] = ids[b] as string;
    ids[b] = temp as string;
    manager.reorderPhotos(ids);
  };

  const handleMoveLeft = (index: number) => {
    if (index === 0) return;
    swapPhotos(index - 1, index);
  };

  const handleMoveRight = (index: number) => {
    if (index === photos.length - 1) return;
    swapPhotos(index, index + 1);
  };

  return (
    <div className="flex flex-col gap-4">
      {photos.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {photos.map((photo, index) => (
            <div key={photo.id} className="group relative overflow-hidden rounded-lg border border-primary">
              {photo.image ? (
                <img src={photo.image.src} alt="" className="aspect-square w-full object-cover" />
              ) : (
                <div className="flex aspect-square items-center justify-center bg-secondary">
                  <span className="text-sm text-tertiary">No preview</span>
                </div>
              )}
              {index === 0 && (
                <div className="absolute left-2 top-2">
                  <Badge color="brand" size="sm">
                    Cover
                  </Badge>
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/60 to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100">
                <div className="flex gap-1">
                  <button
                    type="button"
                    className="rounded-md bg-white/90 p-1 text-secondary hover:bg-white disabled:opacity-40"
                    onClick={() => handleMoveLeft(index)}
                    disabled={index === 0}
                    aria-label="Move left"
                  >
                    <ArrowLeft className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    className="rounded-md bg-white/90 p-1 text-secondary hover:bg-white disabled:opacity-40"
                    onClick={() => handleMoveRight(index)}
                    disabled={index === photos.length - 1}
                    aria-label="Move right"
                  >
                    <ArrowRight className="size-3.5" />
                  </button>
                </div>
                <button
                  type="button"
                  className="rounded-md bg-white/90 p-1 text-error-600 hover:bg-white"
                  onClick={() => manager.requestDelete(photo.id)}
                  aria-label="Delete photo"
                >
                  <Trash01 className="size-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {isUploadModalOpen ? (
        <div className="flex flex-col gap-3">
          <FileUpload.Root>
            <FileUpload.DropZone
              accept="image/jpeg,image/png,image/webp"
              allowsMultiple
              maxSize={10 * 1024 * 1024}
              hint="JPG, PNG or WebP (max. 10MB each)"
              isDisabled={false}
              onDropFiles={handleFileDrop}
            />
            {upload.files.length > 0 && (
              <FileUpload.List>
                {upload.files.map((f) => (
                  <FileUpload.ListItemProgressBar
                    key={f.id}
                    name={f.file.name}
                    size={f.file.size}
                    progress={f.progress}
                    type="img"
                  />
                ))}
              </FileUpload.List>
            )}
          </FileUpload.Root>
          <div className="flex gap-2">
            <Button
              size="sm"
              color="primary"
              isDisabled={upload.completedSignedIds.length === 0 || upload.isUploading}
              onClick={confirmUpload}
            >
              Upload {upload.completedSignedIds.length > 0 ? `(${upload.completedSignedIds.length})` : ""}
            </Button>
            <Button size="sm" color="secondary" onClick={closeUploadModal}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <Button size="sm" color="secondary" iconLeading={Plus} onClick={openUploadModal}>
            Upload photos
          </Button>
          {hasStockPhotoSupport && (
            <p className="text-sm text-tertiary">
              or{" "}
              <button
                type="button"
                onClick={() => setIsStockSearchOpen(true)}
                className="font-medium text-brand-600 hover:text-brand-700"
              >
                search stock photos
              </button>
            </p>
          )}
        </div>
      )}

      {manager.deleteTarget && (
        <div className="flex items-center gap-3 rounded-lg border border-error-300 bg-error-25 p-3">
          <p className="flex-1 text-sm text-error-700">Delete this photo? This cannot be undone.</p>
          <Button size="sm" color="primary-destructive" onClick={manager.confirmDelete}>
            Delete
          </Button>
          <Button size="sm" color="secondary" onClick={manager.cancelDelete}>
            Cancel
          </Button>
        </div>
      )}

      {hasStockPhotoSupport && stockSearchPath && (
        <StockPhotoSearchModal
          isOpen={isStockSearchOpen}
          onOpenChange={setIsStockSearchOpen}
          onSelect={handleStockPhotoSelect}
          searchPath={stockSearchPath}
        />
      )}
    </div>
  );
}
